import React, { useState, useEffect, useCallback } from 'react';
import { getRebillJobDetails } from '../api';

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris' }) : '—';

const statusColor = (s) => {
  const map = {
    pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    processing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    completed: 'bg-green-500/10 text-green-400 border-green-500/20',
    declined: 'bg-red-500/10 text-red-400 border-red-500/20',
    failed: 'bg-red-500/10 text-red-400 border-red-500/20',
    no_gateway: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    cancelled: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    success: 'bg-green-500/10 text-green-400 border-green-500/20',
    error: 'bg-red-500/10 text-red-400 border-red-500/20',
    running: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    paused: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  };
  return map[s] || 'bg-slate-500/10 text-slate-400 border-slate-500/20';
};

const TABS = [
  { key: 'overview', label: '📊 Overview' },
  { key: 'scheduled', label: '📋 Scheduled' },
  { key: 'nmi', label: '💳 NMI Transactions' },
  { key: 'routing', label: '🔀 Gateway Routing' },
];

function JobDetailsModal({ jobId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedTxn, setExpandedTxn] = useState(null);
  const [scheduledFilter, setScheduledFilter] = useState('all');

  const fetchDetails = useCallback(async () => {
    try {
      const res = await getRebillJobDetails(jobId);
      if (res.success) setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => {
    fetchDetails();
    const i = setInterval(fetchDetails, 8000);
    return () => clearInterval(i);
  }, [fetchDetails]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (loading) return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
    </div>
  );

  if (!data) return null;

  const { job, scheduled_tasks, transactions, triplets, summary } = data;

  const filteredTasks = scheduledFilter === 'all' ? scheduled_tasks : scheduled_tasks.filter(t => t.status === scheduledFilter);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">{job.name || `Job #${job.id}`}</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor(job.status)}`}>{job.status}</span>
              <span className="text-xs text-slate-400">Created {fmtDate(job.created_at)}</span>
              {job.started_at && <span className="text-xs text-slate-400">• Started {fmtDate(job.started_at)}</span>}
              {job.completed_at && <span className="text-xs text-slate-400">• Completed {fmtDate(job.completed_at)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none px-2">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700 px-4">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}>{tab.label}</button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && <OverviewTab job={job} summary={summary} transactions={transactions} />}
          {activeTab === 'scheduled' && (
            <ScheduledTab tasks={filteredTasks} allTasks={scheduled_tasks} filter={scheduledFilter} setFilter={setScheduledFilter} summary={summary} />
          )}
          {activeTab === 'nmi' && (
            <NmiTab transactions={transactions} expandedTxn={expandedTxn} setExpandedTxn={setExpandedTxn} />
          )}
          {activeTab === 'routing' && <RoutingTab triplets={triplets} tasks={scheduled_tasks} job={job} />}
        </div>
      </div>
    </div>
  );
}

/* =============== OVERVIEW TAB =============== */
function OverviewTab({ job, summary, transactions }) {
  const pricepoints = job.pricepoints || [];
  const gatewayIds = job.gateway_ids || [];

  return (
    <div className="space-y-5">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-center">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">Approval Rate</p>
          <p className={`text-2xl font-bold mt-1 ${
            summary.approval_rate >= 50 ? 'text-green-400' : summary.approval_rate >= 30 ? 'text-yellow-400' : summary.approval_rate !== null ? 'text-red-400' : 'text-slate-500'
          }`}>{summary.approval_rate !== null ? `${summary.approval_rate}%` : '—'}</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-center">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">Revenue</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{fmt(job.total_revenue)}</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-center">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">Success</p>
          <p className="text-2xl font-bold text-green-400 mt-1">{summary.completed}</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-center">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">Total Declined</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{summary.declined}</p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-center">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">Total Planned</p>
          <p className="text-2xl font-bold text-white mt-1">{summary.total_tasks}</p>
        </div>
      </div>

      {/* Decline Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-yellow-400 text-sm">♻️</span>
            <p className="text-[11px] text-yellow-400 font-semibold uppercase tracking-wider">Retryable Declines</p>
          </div>
          <p className="text-xl font-bold text-yellow-400">{summary.retryable_declines || 0}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Insufficient Funds / Activity Limit → Recycle Billing</p>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-red-400 text-sm">🚫</span>
            <p className="text-[11px] text-red-400 font-semibold uppercase tracking-wider">Hard Declines</p>
          </div>
          <p className="text-xl font-bold text-red-400">{summary.hard_declines || 0}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Do Not Honor, Lost/Stolen, etc.</p>
        </div>
        <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-purple-400 text-sm">🛡️</span>
            <p className="text-[11px] text-purple-400 font-semibold uppercase tracking-wider">Blacklisted</p>
          </div>
          <p className="text-xl font-bold text-purple-400">{summary.blacklisted || 0}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Skipped — CB360 chargeback/alert match</p>
        </div>
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-orange-400 text-sm">⚠️</span>
            <p className="text-[11px] text-orange-400 font-semibold uppercase tracking-wider">No Gateway</p>
          </div>
          <p className="text-xl font-bold text-orange-400">{summary.no_gateway || 0}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">All gateways excluded by triplet rule</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
        <div className="flex justify-between text-xs text-slate-400 mb-2">
          <span>Progress</span>
          <span>{job.processed || 0} / {job.total_clients || 0} ({summary.total_tasks > 0 ? Math.round(((job.processed || 0) / summary.total_tasks) * 100) : 0}%)</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
          <div className="h-full flex">
            <div className="bg-green-500 transition-all" style={{ width: `${summary.total_tasks > 0 ? (summary.completed / summary.total_tasks) * 100 : 0}%` }} />
            <div className="bg-red-500 transition-all" style={{ width: `${summary.total_tasks > 0 ? (summary.declined / summary.total_tasks) * 100 : 0}%` }} />
            <div className="bg-orange-500 transition-all" style={{ width: `${summary.total_tasks > 0 ? (summary.no_gateway / summary.total_tasks) * 100 : 0}%` }} />
          </div>
        </div>
        <div className="flex gap-4 mt-2 text-[10px]">
          <span className="text-green-400">● Success {summary.completed}</span>
          <span className="text-red-400">● Declined {summary.declined}</span>
          <span className="text-orange-400">● No GW {summary.no_gateway}</span>
          <span className="text-yellow-400">● Pending {summary.pending}</span>
        </div>
      </div>

      {/* Job Config */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Job Configuration</h4>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-400">Date Range</span><span className="text-white">{job.date_from ? new Date(job.date_from).toLocaleDateString() : '?'} → {job.date_to ? new Date(job.date_to).toLocaleDateString() : '?'}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Duration</span><span className="text-white">{job.duration_days} day(s)</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Target Cycle</span><span className="text-white">{job.target_cycle === -1 ? 'No Gateway' : job.target_cycle !== null ? `Cycle ${job.target_cycle}` : 'All Pending'}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Selected Gateways</span><span className="text-white">{gatewayIds.length}</span></div>
          </div>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Pricepoints</h4>
          <div className="space-y-2">
            {pricepoints.map((pp, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-emerald-400 font-mono font-bold">{fmt(pp.amount)}</span>
                <div className="flex-1 mx-3 bg-slate-700 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-blue-500 h-full rounded-full" style={{ width: `${pp.percentage}%` }} />
                </div>
                <span className="text-slate-400">{pp.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =============== SCHEDULED TAB =============== */
function ScheduledTab({ tasks, allTasks, filter, setFilter, summary }) {
  const filters = [
    { key: 'all', label: 'All', count: allTasks.length },
    { key: 'pending', label: 'Pending', count: summary.pending },
    { key: 'completed', label: 'Completed', count: summary.completed },
    { key: 'declined', label: 'Declined', count: summary.declined },
    { key: 'no_gateway', label: 'No Gateway', count: summary.no_gateway },
    { key: 'failed', label: 'Failed', count: summary.failed },
    { key: 'cancelled', label: 'Cancelled', count: summary.cancelled },
  ];

  return (
    <div className="space-y-4">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              filter === f.key ? 'bg-blue-600/20 border-blue-500 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
            }`}>{f.label} <span className="ml-1 opacity-60">({f.count})</span></button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-800 z-10">
              <tr>
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-500 uppercase">ID</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-500 uppercase">Client</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-500 uppercase">Card</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-500 uppercase">Amount</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-500 uppercase">Gateway</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-500 uppercase">Scheduled</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-500 uppercase">Status</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-500 uppercase">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {tasks.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No tasks matching filter</td></tr>
              ) : tasks.map(t => (
                <tr key={t.id} className="hover:bg-slate-700/20">
                  <td className="px-3 py-2 text-slate-500 font-mono">#{t.id}</td>
                  <td className="px-3 py-2 text-white">{t.client_name}</td>
                  <td className="px-3 py-2 text-slate-300 font-mono">{t.client_card}</td>
                  <td className="px-3 py-2 text-center text-emerald-400 font-mono">{t.amount ? fmt(t.amount) : '—'}</td>
                  <td className="px-3 py-2 text-slate-300 truncate max-w-[180px]" title={t.gateway_name}>{t.gateway_name}</td>
                  <td className="px-3 py-2 text-center text-slate-400">{fmtDate(t.scheduled_time)}</td>
                  <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColor(t.status)}`}>{t.status}</span></td>
                  <td className="px-3 py-2 text-red-400 truncate max-w-[150px]" title={t.error_message}>{t.error_message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* =============== NMI TRANSACTIONS TAB =============== */
function NmiTab({ transactions, expandedTxn, setExpandedTxn }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400">{transactions.length} NMI transaction(s) recorded for this job</p>

      {transactions.length === 0 ? (
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-8 text-center text-slate-500">
          No NMI transactions yet. Transactions appear here as they are processed.
        </div>
      ) : (
        <div className="space-y-2">
          {transactions.map(tx => (
            <div key={tx.id} className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
              {/* Transaction header — clickable */}
              <button onClick={() => setExpandedTxn(expandedTxn === tx.id ? null : tx.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${tx.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-white text-xs font-medium">{tx.client_name}</span>
                  <span className="text-slate-500 text-[10px] font-mono">{tx.client_card}</span>
                  <span className="text-emerald-400 text-xs font-mono font-bold">{fmt(tx.amount)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400 text-[10px] truncate max-w-[200px]">{tx.gateway_name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColor(tx.status)}`}>{tx.status}</span>
                  <span className="text-slate-500 text-[10px]">{fmtDate(tx.created_at)}</span>
                  <span className="text-slate-500 text-xs">{expandedTxn === tx.id ? '▾' : '▸'}</span>
                </div>
              </button>

              {/* Expanded details */}
              {expandedTxn === tx.id && (
                <div className="border-t border-slate-700 px-4 py-4 space-y-4">
                  {/* NMI Request Summary */}
                  <div>
                    <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">NMI Request</h5>
                    <div className="bg-slate-900 rounded-lg p-3 font-mono text-[11px] text-slate-300 space-y-1">
                      <p><span className="text-blue-400">POST</span> <span className="text-slate-500">https://secure.nmi.com/api/transact.php</span></p>
                      <p className="text-slate-500">type=<span className="text-white">sale</span></p>
                      <p className="text-slate-500">amount=<span className="text-emerald-400">{tx.amount}</span></p>
                      <p className="text-slate-500">ccnumber=<span className="text-white">{tx.client_card}</span></p>
                      <p className="text-slate-500">gateway=<span className="text-yellow-400">{tx.gateway_name}</span> <span className="text-slate-600">(NMI ID: {tx.gateway_nmi_id})</span></p>
                      <p className="text-slate-500">client_bin=<span className="text-cyan-400">{tx.client_bin}</span></p>
                    </div>
                  </div>

                  {/* NMI Response */}
                  <div>
                    <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">NMI Response</h5>
                    <div className="bg-slate-900 rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-slate-500 block text-[10px]">Status</span><span className={tx.status === 'success' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>{tx.status.toUpperCase()}</span></div>
                        <div><span className="text-slate-500 block text-[10px]">Response Code</span><span className="text-white font-mono">{tx.response_code || '—'}</span></div>
                        <div><span className="text-slate-500 block text-[10px]">Response Text</span><span className="text-white">{tx.response_text || '—'}</span></div>
                        <div><span className="text-slate-500 block text-[10px]">Transaction ID</span><span className="text-cyan-400 font-mono">{tx.transaction_id_nmi || '—'}</span></div>
                      </div>

                      {/* Full NMI Response */}
                      {tx.full_response && Object.keys(tx.full_response).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-300">Show full NMI response ({Object.keys(tx.full_response).length} fields)</summary>
                          <div className="mt-2 bg-slate-950 rounded p-2 max-h-48 overflow-auto">
                            <table className="w-full text-[10px] font-mono">
                              <tbody>
                                {Object.entries(tx.full_response).map(([key, val]) => (
                                  <tr key={key} className="border-b border-slate-800">
                                    <td className="py-0.5 pr-3 text-blue-400 whitespace-nowrap">{key}</td>
                                    <td className="py-0.5 text-slate-300 break-all">{String(val)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>

                  {/* Routing Info */}
                  <div>
                    <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Routing Decision</h5>
                    <div className="bg-slate-900 rounded-lg p-3 text-xs space-y-1">
                      <p className="text-slate-400">Gateway: <span className="text-white">{tx.processor} - {tx.bank} - {tx.dba}</span></p>
                      <p className="text-slate-400">NMI Gateway ID: <span className="text-cyan-400 font-mono">{tx.gateway_nmi_id}</span></p>
                      <p className="text-slate-400">Client BIN: <span className="text-yellow-400 font-mono">{tx.client_bin}</span></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =============== GATEWAY ROUTING TAB =============== */
function RoutingTab({ triplets, tasks, job }) {
  // Group triplets by client_id
  const tripletsByClient = {};
  for (const t of triplets) {
    if (!tripletsByClient[t.client_id]) tripletsByClient[t.client_id] = [];
    tripletsByClient[t.client_id].push(t);
  }

  // Group tasks by gateway
  const gatewayStats = {};
  for (const t of tasks) {
    const key = t.gateway_name || 'Not Assigned';
    if (!gatewayStats[key]) gatewayStats[key] = { total: 0, completed: 0, declined: 0, no_gateway: 0, pending: 0, failed: 0 };
    gatewayStats[key].total++;
    if (t.status === 'completed') gatewayStats[key].completed++;
    else if (t.status === 'declined') gatewayStats[key].declined++;
    else if (t.status === 'no_gateway') gatewayStats[key].no_gateway++;
    else if (t.status === 'pending') gatewayStats[key].pending++;
    else if (t.status === 'failed') gatewayStats[key].failed++;
  }

  // Clients with their initial triplet (original gateway)
  const clientsWithRouting = tasks.filter(t => t.gateway_id).map(t => ({
    client_name: t.client_name,
    client_bin: t.client_bin,
    initial_triplet: `${t.client_initial_processor} - ${t.client_initial_bank} - ${t.client_initial_dba}`,
    routed_to: t.gateway_name,
    status: t.status,
    triplets_used: tripletsByClient[t.client_id] || [],
  }));

  return (
    <div className="space-y-5">
      {/* Gateway Distribution */}
      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Gateway Distribution</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(gatewayStats).map(([gw, stats]) => (
            <div key={gw} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
              <p className="text-xs text-white font-medium truncate mb-2" title={gw}>{gw}</p>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-slate-400">Total: {stats.total}</span>
                <span className="text-green-400">✓ {stats.completed}</span>
                <span className="text-red-400">✗ {stats.declined}</span>
                <span className="text-yellow-400">⏳ {stats.pending}</span>
              </div>
              <div className="mt-2 w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                <div className="h-full flex">
                  <div className="bg-green-500" style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }} />
                  <div className="bg-red-500" style={{ width: `${stats.total > 0 ? (stats.declined / stats.total) * 100 : 0}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Triplet Exclusion Log */}
      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Triplet Routing Log</h4>
        <p className="text-[10px] text-slate-500 mb-2">Shows how each client was routed away from their initial gateway triplet (Processor/Bank/DBA) to avoid conflicts.</p>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[40vh] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-slate-800 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-slate-500 uppercase">Client</th>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-slate-500 uppercase">BIN</th>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-slate-500 uppercase">Original Triplet</th>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-slate-500 uppercase">→ Routed To</th>
                  <th className="px-3 py-2 text-center text-[10px] font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-center text-[10px] font-medium text-slate-500 uppercase">Used Triplets</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {clientsWithRouting.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No routing data yet</td></tr>
                ) : clientsWithRouting.slice(0, 200).map((c, i) => (
                  <tr key={i} className="hover:bg-slate-700/20">
                    <td className="px-3 py-2 text-white">{c.client_name}</td>
                    <td className="px-3 py-2 text-cyan-400 font-mono">{c.client_bin}</td>
                    <td className="px-3 py-2 text-red-400/70 line-through text-[10px]">{c.initial_triplet}</td>
                    <td className="px-3 py-2 text-green-400 text-[10px]">{c.routed_to}</td>
                    <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColor(c.status)}`}>{c.status}</span></td>
                    <td className="px-3 py-2 text-center text-slate-400">{c.triplets_used.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobDetailsModal;
